import { useEffect, useState } from "react"
import Tabs from "aldoo-ra/Tabs"
import TabRail from "./tab-rail"
import { groupIconElement } from "./group-icon"

/**
 * The tab in the url, when the editor asked for it (`tabParam`): the page can
 * then be linked and reloaded on the tab it was left on.
 */
const tabInUrl = (param) => {
  if (!param || typeof window === "undefined") return null
  return new URLSearchParams(window.location.search).get(param)
}

const writeTabToUrl = (param, value) => {
  if (!param || typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (url.searchParams.get(param) === value) return
  url.searchParams.set(param, value)
  window.history.replaceState({}, "", url)
}

export default function GroupRenderTabs({
  namedGroups,
  ungroupedGroup,
  renderGroupFields,
  fieldSearchData,
  renderSearchResults,
  tabParam,
  // "horizontal" (the default) or "vertical": a rail on the left, which a model
  // composed of many extensions needs
  tabsOrientation = "horizontal",
  // the help center's key of a tab (TabRail helpKeyOf)
  helpKeyOf = null,
}) {
  const known = (id) => id === "__ungrouped__" || namedGroups.some((g) => g.id === id)
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = tabInUrl(tabParam)
    if (fromUrl && known(fromUrl)) return fromUrl
    return namedGroups[0]?.id ?? "__ungrouped__"
  })

  // a group that arrives later (an extension, a lazily loaded type) may be the
  // one the url asks for
  useEffect(() => {
    const fromUrl = tabInUrl(tabParam)
    if (fromUrl && fromUrl !== activeTab && known(fromUrl)) setActiveTab(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namedGroups.length])

  const selectTab = (value) => {
    setActiveTab(value)
    writeTabToUrl(tabParam, value)
  }

  if (fieldSearchData?.hasActiveSearch) {
    return renderSearchResults()
  }

  // No named groups — render fields flat without tab chrome
  if (!namedGroups.length) {
    return <div>{renderGroupFields(ungroupedGroup)}</div>
  }

  const tabs = namedGroups.map((g) => ({ value: g.id, label: g.label, ...(g.badge ? { badge: g.badge } : {}) }))

  // Add a General tab for ungrouped fields if any exist
  const hasUngrouped = ungroupedGroup._hasContent
  if (hasUngrouped) {
    tabs.push({ value: "__ungrouped__", label: "General" })
  }

  const activeGroup =
    activeTab === "__ungrouped__"
      ? ungroupedGroup
      : namedGroups.find((g) => g.id === activeTab)

  const tabsWithIcons = namedGroups.some((g) => g.icon)
    ? tabs.map((tab) => ({ ...tab, icon: groupIconElement(namedGroups.find((g) => g.id === tab.value)?.icon) }))
    : tabs

  if (tabsOrientation === "vertical") {
    return (
      // on a phone the rail is a bar above the fields (TabRail), so the two stack
      <div className="flex flex-col md:flex-row md:gap-4 items-stretch h-full min-h-0">
        <TabRail tabs={tabsWithIcons} value={activeTab} onChange={selectTab} helpKeyOf={helpKeyOf} />
        <div className="min-h-[200px] flex-1 min-w-0 w-full max-w-full overflow-x-hidden [&_img]:max-w-full [&_table]:w-full [&_table]:max-w-full [&_input]:max-w-full [&_textarea]:max-w-full overflow-y-auto h-full md:pr-2 pb-24 scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
          {activeGroup && renderGroupFields(activeGroup)}
        </div>
      </div>
    )
  }

  return (
    <div>
      <Tabs
        tabs={tabsWithIcons}
        value={activeTab}
        onChange={selectTab}
        variant="admin"
        className="mb-4"
      />
      <div className="min-h-[200px]">
        {activeGroup && renderGroupFields(activeGroup)}
      </div>
    </div>
  )
}
